import React, { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Container } from 'reactstrap';
import { skipToken } from '@reduxjs/toolkit/query';

import { faExclamationTriangle } from '~components/icon';
import { TableView } from '~components/table-view';

import getMissingQualificationsColumns from '~weekly-planner/forms/missing-qualifications/list';

import { useGetMissingQualificationsQuery } from '~weekly-planner/actions/appointments/missingQualifications';

import { clear, updateParams } from '~weekly-planner/reducers/appointments/missingQualifications';

import {
  selectDerivedWeekRange,
  selectShowSystemUsers,
  selectOptions as selectWeeklyPlannerOptions,
} from '~weekly-planner/selectors';
import {
  selectCount as selectMissingQualificationCount,
  selectAll as selectMissingQualifications,
  selectParams,
} from '~weekly-planner/selectors/appointments/missingQualifications';

import MissingQualificationsViewMenu from './MissingQualificationViewMenu';

interface ComponentProps {
  isOpen: boolean;
}

const MissingQualifications: React.FC<ComponentProps> = React.memo(({ isOpen }) => {
  const dispatch = useAppDispatch();
  const params = useAppSelector(selectParams);
  const appointments = useAppSelector(selectMissingQualifications);
  const count = useAppSelector(selectMissingQualificationCount);
  const { missingQualificationsExtendedQualifications } = useAppSelector(selectWeeklyPlannerOptions);
  const showSystemUsers = useAppSelector(selectShowSystemUsers);
  const showSystemUsersParam = showSystemUsers ? { show_system_users: true } : {};

  const derivedWeekRange = useAppSelector(selectDerivedWeekRange);
  const { week_start } = derivedWeekRange;
  const { limit } = params;

  // RTK Query Args
  const queryArgs = useMemo(
    () => ({
      limit,
      week_start: params?.week_start,
      ...showSystemUsersParam,
    }),
    [params, showSystemUsers],
  );

  // RTK Query Hook
  const { isFetching } = useGetMissingQualificationsQuery(params?.week_start && isOpen ? queryArgs : skipToken, {
    refetchOnMountOrArgChange: true,
  });

  const onLoadMore = () => {
    const newLimit = limit + 30;
    dispatch(updateParams({ ...params, limit: newLimit }));
  };

  useEffect(() => {
    if (isOpen && week_start) {
      dispatch(updateParams({ week_start, limit: 30, ...showSystemUsersParam }));
    }
  }, [isOpen, showSystemUsers, week_start]);

  // unmount & clear reducer
  useEffect(() => {
    return () => {
      dispatch(clear([]));
    };
  }, []);

  // Get columns with the component's dispatch function
  const missingQualificationsColumns = getMissingQualificationsColumns(missingQualificationsExtendedQualifications);

  // Memoise the mapped data so id-less rows keep a stable randomUUID across renders
  const tableData = useMemo(
    () =>
      appointments.map((appt) => ({
        ...appt,
        id: appt.id?.toString() ?? appt.key ?? crypto.randomUUID(),
      })),
    [appointments],
  );

  return (
    <div className="missing-qualifications-list">
      <Container className="inner-content limited">
        <div className="nav">
          <MissingQualificationsViewMenu />
        </div>
        <TableView
          id="missing-qualifications"
          data={tableData}
          columns={missingQualificationsColumns}
          readonly
          striped
          isLoading={isFetching}
          allowPagination={true}
          onLoadMore={onLoadMore}
          totalCount={count}
          emptyIcon={faExclamationTriangle}
          emptyMessage="No missing qualifications found"
        />
      </Container>
    </div>
  );
});

MissingQualifications.displayName = 'MissingQualifications';
export default MissingQualifications;
